import apiService from './ApiService'
import clarityService from './ClarityService'
import _ from 'lodash'

class ActivityTracker {
  constructor() {
    this.sessionStart = Date.now()
    this.lastActivity = Date.now()
    this.currentPage = null
    this.pageStartTime = Date.now()
    this.isInitialized = false
    this.pendingActivities = []
    this.clarityEnabled = true // Flag to coordinate with Clarity
    this.sessionEndTracked = false // Prevent duplicate session end
    this.flushInterval = null // Track interval reference
  }

  initialize() {
    if (this.isInitialized) {
      console.log('ActivityTracker already initialized')
      return
    }

    this.isInitialized = true
    this.sessionEndTracked = false // Reset for new session
    this.sessionStart = Date.now() // Reset session start time
    this.setupTracking()

    // Track initial page view
    this.currentPage = window.location.pathname + window.location.search
    this.pageStartTime = Date.now()
    this.trackPageView()
  }

  setupTracking() {
    // Track user interactions
    document.addEventListener('click', this.trackInteraction.bind(this))
    document.addEventListener('scroll', this.trackScroll.bind(this))
    document.addEventListener('keypress', this.trackInteraction.bind(this))

    // Track when user becomes active/inactive
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange.bind(this),
    )

    // Track session end - using visibilitychange instead of beforeunload for better reliability
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // More reliable than beforeunload
        this.trackSessionEndSync()
      }
    })

    // Keep beforeunload as backup (with sendBeacon)
    window.addEventListener('beforeunload', this.trackSessionEndSync.bind(this)) // Browser tab closing
    window.addEventListener('pagehide', this.trackSessionEndSync.bind(this))

    // Periodic activity flush
    this.flushInterval = setInterval(
      this.flushPendingActivities.bind(this),
      30000,
    ) // Every 30 seconds
  }

  handleRouteChange(newPath) {
    if (!this.isInitialized) return

    const fullPath = newPath

    // Track duration on previous page
    if (this.currentPage && this.currentPage !== fullPath) {
      this.trackPageDuration()
    }

    // Update current page and track new page view
    this.currentPage = fullPath
    this.pageStartTime = Date.now()
    this.trackPageView()
  }

  // Rule: NEVER manually call
  // 1. Initial Page Load: ActivityTracker.initialize() calls trackPageView()
  // 2. Route Changes: useRouteTracking hook calls handleRouteChange() → trackPageView()
  async trackPageView() {
    const fullPath = window.location.pathname + window.location.search // ignoring window.location.hash otherwise same page hash changes will be tracked as new page views
    const activity = {
      type: 'page_view',
      title: document.title,
      path: fullPath,
      // search: window.location.search,
      timestamp: new Date().toISOString(),
      metadata: {
        referrer: document.referrer || null,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      },
    }

    await this.queueActivity(activity)
    if (this.clarityEnabled) {
      clarityService.trackPageView(activity.path, activity.metadata)
    }
  }

  async trackPageDuration() {
    if (!this.currentPage) return

    const duration = Date.now() - this.pageStartTime
    if (duration > 1000) {
      // Only track if spent more than 1 second
      const activity = {
        type: 'page_duration',
        path: this.currentPage,
        duration: duration,
        timestamp: new Date().toISOString(),
      }

      await this.queueActivity(activity)
      if (this.clarityEnabled) {
        clarityService.trackEvent('page_engagement', {
          path: this.currentPage,
          duration: duration,
          engagementLevel:
            duration > 30000 ? 'high' : duration > 10000 ? 'medium' : 'low',
        })
      }
    }
  }

  trackInteraction(event) {
    this.lastActivity = Date.now()

    // Track specific interaction types
    if (event.type === 'click') {
      const element = event.target
      const elementType = element.tagName?.toLowerCase()
      const elementClass = element.className
      const elementId = element.id

      // Track important clicks
      if (
        elementType === 'button' ||
        elementType === 'a' ||
        element.role === 'button'
      ) {
        const activity = {
          type: 'interaction',
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
          metadata: {
            action: 'click',
            element: elementType,
            elementClass,
            elementId,
            text: element.textContent?.substring(0, 100),
          },
        }
        this.queueActivity(activity)
        // Track significant clicks in Clarity
        if (this.clarityEnabled) {
          clarityService.trackEvent('ui_interaction', {
            action: 'click',
            element: elementType,
            text: element.textContent?.substring(0, 50),
          })
        }
      }
    }
  }

  trackScroll = _.debounce(() => {
    this.lastActivity = Date.now()

    const scrollPercentage = Math.round(
      (window.scrollY /
        (document.documentElement.scrollHeight - window.innerHeight)) *
        100,
    )

    if (scrollPercentage % 25 === 0 && scrollPercentage > 0) {
      // Track at 25%, 50%, 75%, 100%
      const activity = {
        type: 'scroll',
        path: window.location.pathname,
        timestamp: new Date().toISOString(),
        metadata: {
          percentage: scrollPercentage,
        },
      }
      this.queueActivity(activity)
      // Track scroll milestones in Clarity
      if (this.clarityEnabled) {
        clarityService.trackEvent('scroll_milestone', {
          percentage: scrollPercentage,
          path: window.location.pathname,
        })
      }
    }
  }, 1000)

  handleVisibilityChange() {
    if (document.hidden) {
      // User switched away from tab
      this.trackPageDuration()
      // Track tab switch in Clarity
      if (this.clarityEnabled) {
        clarityService.trackEvent('tab_hidden', {
          timestamp: new Date().toISOString(),
        })
      }
    } else {
      // User returned to tab
      this.pageStartTime = Date.now()
      this.lastActivity = Date.now()

      // Track tab return in Clarity
      if (this.clarityEnabled) {
        clarityService.trackEvent('tab_visible', {
          timestamp: new Date().toISOString(),
        })
      }
    }
  }

  // ASYNC version for destroy() to await
  async trackSessionEnd() {
    // Prevent duplicate session end tracking
    if (this.sessionEndTracked) return
    this.sessionEndTracked = true

    const sessionDuration = Date.now() - this.sessionStart

    // Track final page duration
    await this.trackPageDuration()

    const activity = {
      type: 'session_end',
      timestamp: new Date().toISOString(),
      metadata: {
        sessionDuration,
        totalPages: this.getUniquePages(),
      },
    }

    // Force immediate send for session end
    try {
      await apiService.post('/api/v1/activity/track', [activity])
      if (this.clarityEnabled) {
        clarityService.trackEvent('session_end', {
          sessionDuration,
          totalPages: this.getUniquePages(),
        })
      }
    } catch (error) {
      console.error('Failed to track session end:', error)
    }

    // Clear interval
    if (this.flushInterval) {
      clearInterval(this.flushInterval)
      this.flushInterval = null
    }
  }

  // SYNC version for beforeunload/pagehide (uses sendBeacon)
  trackSessionEndSync() {
    if (this.sessionEndTracked) {
      console.log('Session end already tracked (sync)')
      return
    }

    this.sessionEndTracked = true
    console.log('Tracking session end (sync with sendBeacon)...')

    const sessionDuration = Date.now() - this.sessionStart

    const activity = {
      type: 'session_end',
      timestamp: new Date().toISOString(),
      metadata: {
        sessionDuration,
        totalPages: this.getUniquePages(),
      },
    }

    // Use sendBeacon for reliability during page unload
    try {
      const blob = new Blob([JSON.stringify([activity])], {
        type: 'application/json',
      })

      // Get the full API URL
      const apiUrl = `${window.location.origin}/api/v1/activity/track`

      if (navigator.sendBeacon) {
        const sent = navigator.sendBeacon(apiUrl, blob)
        console.log('Session end sent via sendBeacon:', sent)
      } else {
        // Fallback for browsers without sendBeacon
        console.warn('sendBeacon not supported, session end might be lost')
      }
    } catch (error) {
      console.error('Failed to track session end (sync):', error)
    }

    if (this.clarityEnabled) {
      clarityService.trackEvent('session_end', {
        sessionDuration,
        totalPages: this.getUniquePages(),
      })
    }
  }

  async queueActivity(activity) {
    this.pendingActivities.push(activity)

    // If queue is getting large, flush immediately
    if (this.pendingActivities.length >= 10) {
      await this.flushPendingActivities()
    }
  }

  async flushPendingActivities() {
    if (this.pendingActivities.length === 0) return

    const activitiesToSend = [...this.pendingActivities]
    this.pendingActivities = []

    try {
      await apiService.post('/api/v1/activity/track', activitiesToSend)
    } catch (error) {
      console.error('Failed to flush activities:', error)
      // Re-queue failed activities (keep only recent ones)
      this.pendingActivities = [
        ...activitiesToSend.slice(-5),
        ...this.pendingActivities,
      ]
    }
  }

  getUniquePages() {
    return new Set(
      this.pendingActivities
        .filter((a) => a.type === 'page_view')
        .map((a) => a.path),
    ).size
  }

  // Public method to manually track custom events
  async trackCustomEvent(eventName, eventData = {}) {
    const activity = {
      type: 'custom_event',
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      metadata: {
        eventName,
        eventData,
      },
    }

    await this.queueActivity(activity)
    if (this.clarityEnabled) {
      clarityService.trackEvent(eventName, eventData)
    }
  }

  // its a subset of "page_view" since it tracks views of specific documents within the site - can be useful if we just need to track document views for each user
  async trackDocumentView(documentType, slug, title, metadata = {}) {
    const fullPath = window.location.pathname + window.location.search // ignoring window.location.hash otherwise same page hash changes will be tracked as new page views
    const activity = {
      type: 'document_view',
      documentType, // 'article', 'decision', 'guide', 'DTAA'
      slug,
      title,
      metadata: {
        ...metadata,
        country: slug.split('-')[0]?.toUpperCase(),
        law: this.extractLawFromSlug(slug),
        referrer: document.referrer || null,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      },
      timestamp: new Date().toISOString(),
      path: fullPath,
    }
    await this.queueActivity(activity)
    if (this.clarityEnabled) {
      clarityService.trackContentView(documentType, slug, title)
    }
  }

  async trackSearchInteraction(
    searchType,
    query,
    filters = null,
    resultsCount = 0,
    breakdown = null,
    extras = {},
  ) {
    const selectedSuggestion =
      typeof extras?.selectedSuggestion === 'string' &&
      extras.selectedSuggestion.trim()
        ? extras.selectedSuggestion.trim()
        : undefined
    const activity = {
      type: 'search_interaction',
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      metadata: {
        searchType, // 'global-search', 'tax-treaties'
        query: query?.trim(),
        filters: filters || {},
        resultsCount: resultsCount || 0,
        // breakdown lets the daily report split totals into per-entity columns
        // (articles/decisions/guidelines for global-search, treaties for
        // tax-treaties). Optional; older rows without it just show blanks.
        breakdown: breakdown || {},
        selectedSuggestion,
      },
    }
    await this.queueActivity(activity)
    if (this.clarityEnabled) {
      clarityService.trackSearch(query?.trim(), resultsCount)
    }
  }

  async trackDocumentAction(action, documentType, slug, title) {
    const activity = {
      type: 'document_action',
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      metadata: {
        action, // 'bookmark', 'unbookmark', 'share', 'print', 'download'
        documentType,
        slug,
        title,
      },
    }
    await this.queueActivity(activity)
    if (this.clarityEnabled) {
      clarityService.trackEvent('document_action', {
        action,
        documentType,
        slug,
        title,
      })
    }
  }

  extractLawFromSlug(slug) {
    const lawNames = {
      'gcc-common-vat-agreement': 'GCC Common VAT Agreement',
      'gcc-common-customs-agreement': 'GCC Common Customs Agreement',
      'gcc-common-excise-agreement': 'GCC Common Excise Agreement',
      'uae-cit-fdl-47-of-2022': 'CIT (FDL No 47 of 2022, as amended)',
      'uae-dmtt-fdl-60-of-2023': 'DMTT (FDL No 60 of 2023)',
      'uae-indirecttax-law-vat-uaevat-8-of-2017-as-amended':
        'VAT (FDL No 8 of 2017, as amended)',
      'uae-tp-fdl-28-of-2022-as-amended':
        'Tax Procedures (FDL No 28 of 2022, as amended)',
      'uae-indirect-excise-7-of-2017-as-amended':
        'Excise Tax (FDL No 7 of 2017, as amended)',
      'uae-fatca-of-2024': 'FATCA (Foreign Account Tax Compliance Act) 2024',
      'uae-crs-federal-decree-48-of-2018':
        'CRS (Common Reporting Standard, Federal Decree No 48 of 2018)',
      'ksa-income-tax-law-of-2004': 'Income Tax Law 2004',
      'ksa-vat-law-m-113-of-2017': 'VAT Law 2017',
      'ksa-customs-law': 'Customs Law',
      'ksa-fatca-of-2017': 'FATCA (Foreign Account Tax Compliance Act) 2017',
      'ksa-excise-law-of-2019': 'Excise Law 2019',
      'ksa-zakat-law-of-1951': 'Zakat Collection Law 1951',
      'kuwait-draft-business-profit-tax-law-of-2024':
        'Draft Business Profit Tax Law 2024',
      'kuwait-kit-law-no-2-of-2008-amending-decree-no-3-of-1955':
        'Income Tax Law - Decree No. 3 of 1955',
      'kuwait-qdmtt-decree-law-157-of-2024':
        'QDMTT Law - Decree No. 157 of 2024',
      'qatar-income-tax-law-24-of-2018': 'Qatar Income Tax Law 24 of 2018',
      'qatar-dmtt-fdl-22-of-2024': 'Qatar DMTT (FDL No 22 of 2024)',
      'qatar-tax-procedures-law': 'Tax Procedures Law',
      'qatar-excise-law-25-of-2018': 'Excise Law 2018',
      'qatar-fatca-of-2014-iga-m1':
        'FATCA (Foreign Account Tax Compliance Act) 2014',
      'oman-income-tax-law-royal-decree-28-of-2009':
        'Income Tax Law (Royal Decree No 28 of 2009, as amended)',
      'oman-vat-law-royal-decree-121-of-2020':
        'VAT Law - Royal Decree No. 121 of 2020',
      'oman-excise-tax-law-royal-decree-23-of-2019':
        'Excise Tax Law (Royal Decree No 23 of 2019)',
      'oman-fatca-of-2014-direct':
        'FATCA (Foreign Account Tax Compliance Act) 2014',
      'oman-cbcr-law': 'Country-by-Country Reporting Law',
      'bahrain-dmtt-law-11-of-2024':
        'Bahrain DMTT Law (Decree Law No. 11 of 2024)',
      'bahrain-vat-law-48-of-2018': 'Bahrain VAT Law 48 of 2018',
    }

    // Find the key in lawNames that the slug starts with
    const matchedKey = Object.keys(lawNames).find((key) => slug.startsWith(key))

    if (matchedKey) {
      // Split the matched key and ignore the first part (country)
      return matchedKey.split('-').slice(1).join('-')
    }

    return null
  }

  async trackNewTabOpen(url, contentType, parentUrl) {
    const activity = {
      type: 'new_tab_open',
      targetUrl: url,
      contentType,
      parentUrl: parentUrl || window.location.href,
      timestamp: new Date().toISOString(),
    }
    await this.queueActivity(activity)
    // Track in Clarity
    if (this.clarityEnabled) {
      clarityService.trackNewTabOpen(url, contentType)
    }
  }

  // Cleanup method
  async destroy() {
    // waits for session end to complete
    if (!this.isInitialized) return
    await this.trackSessionEnd()
    this.isInitialized = false

    // Track final page duration
    // this.trackPageDuration()

    // Remove event listeners
    document.removeEventListener('click', this.trackInteraction)
    document.removeEventListener('scroll', this.trackScroll)
    document.removeEventListener('keypress', this.trackInteraction)
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    )
    window.removeEventListener('beforeunload', this.trackSessionEndSync) // Browser tab closing
    window.removeEventListener('pagehide', this.trackSessionEndSync)

    // Clear interval
    if (this.flushInterval) {
      clearInterval(this.flushInterval)
      this.flushInterval = null
    }

    // Flush any remaining activities (not including session_end, which was already sent)
    await this.flushPendingActivities()
  }
}

// Export singleton instance
export const activityTracker = new ActivityTracker()
export default activityTracker
